import { useEffect, useMemo, useRef, useState } from 'react';

import { LoadingPanel } from './Loader';
import {
  CATEGORIES,
  loadEmoji,
  recentEmoji,
  searchEmoji,
  type Emoji,
  type EmojiSet,
} from '../../lib/emoji';

/**
 * The emoji picker.
 *
 * ## One category at a time
 *
 * There are 1,914 emoji. Rendering all of them is 1,914 buttons in the
 * document, on every composer on the page, whether or not anybody opens the
 * panel — and the largest category is 388, which a browser handles without
 * complaint.
 *
 * So the grid renders the active category only, and search replaces it with
 * matches across the whole set. That avoids virtualisation entirely, which is
 * the alternative and is a lot of machinery for a panel this size.
 *
 * ## The data arrives when the panel does
 *
 * `loadEmoji()` is a dynamic import, so the several hundred kilobytes of names
 * are fetched the first time somebody opens a picker rather than on every page
 * load. It is cached for the session, so this is a one-off flicker of a spinner
 * at most.
 *
 * ## Opens upward by default
 *
 * Composers live at the bottom of the thing they compose into, so a panel that
 * opened downward would be off-screen most of the time.
 */
export function EmojiButton({
  onSelect,
  direction = 'up',
  align = 'right',
  className = '',
}: {
  onSelect: (char: string) => void;
  direction?: 'up' | 'down';
  align?: 'left' | 'right';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;

      if (panel.current?.contains(target) || trigger.current?.contains(target)) return;

      setOpen(false);
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      setOpen(false);
      // Back to the button, so escaping the panel does not drop focus to the
      // top of the document.
      trigger.current?.focus();
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`relative ${className}`}>
      <button
        ref={trigger}
        type="button"
        aria-label="Add emoji"
        aria-expanded={open}
        data-emoji-trigger
        onClick={() => setOpen((current) => !current)}
        className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-ink"
      >
        <svg
          viewBox="0 0 20 20"
          className="h-[18px] w-[18px]"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="7.5" />
          <path d="M7 12a3.6 3.6 0 0 0 6 0" strokeLinecap="round" />
          <circle cx="7.5" cy="8" r=".9" fill="currentColor" stroke="none" />
          <circle cx="12.5" cy="8" r=".9" fill="currentColor" stroke="none" />
        </svg>
      </button>

      {open && (
        <div
          ref={panel}
          data-emoji-panel
          className={[
            'absolute z-50 w-[min(22rem,calc(100vw-2rem))] overflow-hidden',
            'rounded-[var(--radius-card)] border border-line bg-surface shadow-xl',
            direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2',
            align === 'right' ? 'right-0' : 'left-0',
          ].join(' ')}
        >
          <Panel
            onPick={(char) => {
              onSelect(char);
              // Deliberately stays open: people send several in a row, and
              // reopening the panel for each one is the kind of small friction
              // that makes a feature feel grudging.
            }}
          />
        </div>
      )}
    </div>
  );
}

function Panel({ onPick }: { onPick: (char: string) => void }) {
  const [set, setSet] = useState<EmojiSet | null>(null);
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState(CATEGORIES[0]!.group);
  const [recent, setRecent] = useState<string[]>(() => recentEmoji());
  const search = useRef<HTMLInputElement>(null);
  const grid = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    void loadEmoji().then((loaded) => {
      if (!cancelled) setSet(loaded);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Focus lands in the search box, because typing is faster than scrolling for
  // anybody who knows what they are looking for.
  useEffect(() => {
    search.current?.focus();
  }, []);

  const results = useMemo(
    () => (set === null || query.trim() === '' ? null : searchEmoji(set, query)),
    [set, query],
  );
  const shown = results ?? set?.byGroup.get(group) ?? [];

  // Switching category returns to the top; without it, moving from Flags to
  // Smileys leaves you scrolled halfway down a list you have not seen.
  useEffect(() => {
    grid.current?.scrollTo({ top: 0 });
  }, [group, query]);

  const pick = (char: string) => {
    onPick(char);
    setRecent((current) => [char, ...current.filter((c) => c !== char)].slice(0, 24));
  };

  return (
    <>
      <div className="p-2">
        <input
          ref={search}
          type="search"
          aria-label="Search emoji"
          placeholder="Search emoji"
          value={query}
          data-emoji-search
          onChange={(event) => setQuery(event.target.value)}
          className="w-full rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink placeholder:text-faint focus:border-line-strong"
        />
      </div>

      {/* Hidden while searching: the tabs would filter a result set that is
          already filtered, which does nothing anybody expects. */}
      {results === null && (
        <div role="tablist" aria-label="Emoji categories" className="flex gap-0.5 border-b border-line px-2 pb-1">
          {CATEGORIES.map((category) => (
            <button
              key={category.group}
              role="tab"
              type="button"
              aria-selected={group === category.group}
              aria-label={category.label}
              title={category.label}
              data-emoji-category={category.group}
              onClick={() => setGroup(category.group)}
              className={`flex h-8 flex-1 items-center justify-center rounded-t-[var(--radius-control)] text-base transition-colors ${
                group === category.group
                  ? 'border-b-2 border-accent bg-raised'
                  : 'border-b-2 border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <span aria-hidden="true">{category.icon}</span>
            </button>
          ))}
        </div>
      )}

      <div ref={grid} className="h-64 overflow-y-auto p-2" data-emoji-grid>
        {set === null ? (
          <LoadingPanel label="Loading emoji" className="h-full" />
        ) : results !== null && results.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-soft">Nothing matches “{query}”.</p>
        ) : (
          <>
            {/* Only outside a search, and only when there is something to show. */}
            {results === null && recent.length > 0 && (
              <section className="mb-2">
                <h3 className="meta-caps px-1 pb-1">Recent</h3>
                <Grid
                  emoji={recent
                    .map((char) => set?.byChar.get(char))
                    .filter((e): e is Emoji => e !== undefined)}
                  onPick={pick}
                />
              </section>
            )}

            {results === null && (
              <h3 className="meta-caps px-1 pb-1">
                {CATEGORIES.find((c) => c.group === group)?.label ?? group}
              </h3>
            )}

            <Grid emoji={shown} onPick={pick} />
          </>
        )}
      </div>
    </>
  );
}

function Grid({ emoji, onPick }: { emoji: Emoji[]; onPick: (char: string) => void }) {
  return (
    <div className="grid grid-cols-8 gap-0.5">
      {emoji.map((item) => (
        <button
          key={item.char}
          type="button"
          // The name, not the character: a screen reader announcing the glyph
          // reads its Unicode name anyway, but a button with no accessible name
          // announces as "button".
          aria-label={item.name}
          title={item.name}
          data-emoji={item.char}
          onClick={() => onPick(item.char)}
          className="flex aspect-square items-center justify-center rounded-[var(--radius-control)] text-xl leading-none transition-colors hover:bg-raised"
        >
          <span aria-hidden="true">{item.char}</span>
        </button>
      ))}
    </div>
  );
}
